import { useEffect, useState } from "react";
import {
  ApiClientError,
  deferTask,
  fetchInitiative,
  setInitiativeStatus,
  setTaskDone,
  toggleChecklist,
  updateInitiative,
} from "../api/client";
import { StateBanner } from "../components/StateBanner";
import { SourceBadge } from "../components/mk/SourceBadge";
import { Toast } from "../components/mk/Toast";
import {
  CHANNEL_COLOR,
  CHANNEL_LABEL,
  checklistProgress,
  extractSourceDeadlines,
  formatHistoryAt,
  formatMD,
  isOverdue,
  nowJSTTime,
  STATUS_COLOR,
  STATUS_LABEL,
  STATUS_ORDER,
  TASK_TAG_LABEL,
} from "../lib/mkRules";
import { useToast } from "../lib/useToast";
import type {
  History,
  Initiative,
  InitiativeLink,
  InitiativeResult,
  InitiativeStatus,
  InitiativeStatusConflictDetails,
  Task,
} from "../types/contract";

interface InitiativeDetailPageProps {
  id: string;
  onBack: () => void;
}

type FetchState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ok"; today: string; initiative: Initiative; tasks: Task[]; history: History[] };

interface WarnState {
  open_checks: number;
  open_tasks: number;
}

const TYPE_LABEL: Record<string, string> = { recurring: "定期", oneoff: "単発" };

/** 関連資料リンクは http/https のみ許可する（javascript: 等のスキーム注入を防ぐ）。 */
function isHttpUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

export function InitiativeDetailPage({ id, onBack }: InitiativeDetailPageProps) {
  const [state, setState] = useState<FetchState>({ status: "loading" });
  const [warn, setWarn] = useState<WarnState | null>(null);
  const [reviewDraft, setReviewDraft] = useState("");
  const [linkForm, setLinkForm] = useState<{ open: boolean; kind: string; label: string; url: string }>({
    open: false,
    kind: "",
    label: "",
    url: "",
  });
  const [resultsEditing, setResultsEditing] = useState(false);
  const [resultsDraft, setResultsDraft] = useState<InitiativeResult[]>([]);
  const { toastMessage, showToast } = useToast();

  const load = () => {
    fetchInitiative(id)
      .then((res) => {
        setState({ status: "ok", today: res.today, initiative: res.initiative, tasks: res.tasks, history: res.history });
        setReviewDraft(res.initiative.review ?? "");
        setResultsDraft(res.initiative.results);
        setWarn(null);
      })
      .catch((e: unknown) => {
        const message = e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。";
        setState({ status: "error", message });
      });
  };

  useEffect(() => {
    setState({ status: "loading" });
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (state.status === "loading") {
    return (
      <div className="mk-page">
        <div className="skeleton" style={{ height: 80, marginBottom: 12 }} />
        <div className="skeleton" style={{ height: 400 }} />
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div className="mk-page">
        <button className="mk-back-btn" onClick={onBack}>
          ← 一覧へ
        </button>
        <StateBanner kind="error" message={state.message} onRetry={load} />
      </div>
    );
  }

  const { initiative: init, tasks, history, today } = state;
  const color = CHANNEL_COLOR[init.channel];
  const nowTime = nowJSTTime();
  const sortedTasks = [...tasks].sort((a, b) => (a.due_date === b.due_date ? a.due_time.localeCompare(b.due_time) : a.due_date < b.due_date ? -1 : 1));
  const nextTask = sortedTasks.find((t) => !t.done) ?? null;
  const { done: checkDone, total: checkTotal } = checklistProgress(init.checklist);
  const sourceDeadlines = extractSourceDeadlines(init.source_meta);

  const handleStatusClick = (status: InitiativeStatus) => {
    if (status === init.status) return;
    setInitiativeStatus(id, status, false)
      .then(() => {
        showToast(`ステータスを「${STATUS_LABEL[status]}」に変更しました`);
        load();
      })
      .catch((e: unknown) => {
        if (e instanceof ApiClientError && e.status === 422) {
          const details = e.details as InitiativeStatusConflictDetails | undefined;
          // 完了警告バーは open_checks/open_tasks を伴う422（未完了チェック/タスク残り）のときだけ出す。
          // それ以外の422（入力バリデーション等）は通常のエラートーストにする。
          if (details && (details.open_checks !== undefined || details.open_tasks !== undefined)) {
            setWarn({ open_checks: details.open_checks ?? 0, open_tasks: details.open_tasks ?? 0 });
            return;
          }
        }
        showToast(e instanceof ApiClientError ? e.message : "ステータス変更に失敗しました");
      });
  };

  const forceComplete = () => {
    setInitiativeStatus(id, "done", true)
      .then(() => {
        showToast("ステータスを「完了」に変更しました");
        load();
      })
      .catch((e: unknown) => showToast(e instanceof ApiClientError ? e.message : "更新に失敗しました"));
  };

  const toggleTask = (task: Task) => {
    setTaskDone(task.id, !task.done)
      .then(() => {
        if (!task.done) showToast("確認済みにしました");
        load();
      })
      .catch((e: unknown) => showToast(e instanceof ApiClientError ? e.message : "更新に失敗しました"));
  };

  const handleDeferNext = () => {
    if (!nextTask) return;
    deferTask(nextTask.id, 1)
      .then(() => {
        showToast("明日へ延期しました");
        load();
      })
      .catch((e: unknown) => showToast(e instanceof ApiClientError ? e.message : "更新に失敗しました"));
  };

  const handleChecklistToggle = (group: "before" | "during" | "after", index: number, done: boolean) => {
    toggleChecklist(id, group, index, !done)
      .then(load)
      .catch((e: unknown) => showToast(e instanceof ApiClientError ? e.message : "更新に失敗しました"));
  };

  const handleReviewBlur = () => {
    if (reviewDraft === (init.review ?? "")) return;
    updateInitiative(id, { review: reviewDraft }).then(load).catch(() => {
      /* サイレント保存: 失敗時は次回blurで再試行される想定。UIをブロックしない。 */
    });
  };

  const submitLink = () => {
    if (!linkForm.label.trim()) {
      showToast("リンク名を入力してください");
      return;
    }
    const trimmedUrl = linkForm.url.trim();
    if (trimmedUrl && !isHttpUrl(trimmedUrl)) {
      showToast("URLは http:// または https:// で入力してください");
      return;
    }
    const next: InitiativeLink[] = [
      ...init.links,
      { kind: linkForm.kind.trim() || "その他", label: linkForm.label.trim(), url: trimmedUrl || undefined },
    ];
    updateInitiative(id, { links: next })
      .then(() => {
        setLinkForm({ open: false, kind: "", label: "", url: "" });
        load();
      })
      .catch((e: unknown) => showToast(e instanceof ApiClientError ? e.message : "更新に失敗しました"));
  };

  const saveResults = () => {
    updateInitiative(id, { results: resultsDraft })
      .then(() => {
        setResultsEditing(false);
        showToast("成果を保存しました");
        load();
      })
      .catch((e: unknown) => showToast(e instanceof ApiClientError ? e.message : "更新に失敗しました"));
  };

  const addResultRow = () => {
    setResultsDraft((prev) => [...prev, { label: "", value: "" }]);
  };

  return (
    <div className="mk-page">
      <div className="mk-detail-header">
        <button className="mk-back-btn" onClick={onBack}>
          ← 一覧へ
        </button>
        <div className="mk-detail-title-block">
          <div className="mk-detail-title-row">
            <span className="mk-ch-bar" style={{ background: color }} />
            <span className="mk-detail-name">{init.name}</span>
            <span className="mk-detail-channel" style={{ color }}>
              {CHANNEL_LABEL[init.channel]}
            </span>
            <span className="mk-type-chip">{TYPE_LABEL[init.type]}</span>
            <SourceBadge source={init.source} channel={init.channel} />
          </div>
          <div className="mk-detail-meta">
            {formatMD(init.start_date)} — {formatMD(init.end_date)}
            {init.end_open ? "（継続中）" : ""} ／ 担当：{init.owner}
            {init.campaign_tag ? ` ／ キャンペーン：${init.campaign_tag}` : ""}
          </div>
          {init.closed_reason && (
            <div className="mk-closed-reason">
              {init.closed_by === "auto" ? "自動完了" : "完了"}：{init.closed_reason}
              {init.closed_at ? `（${formatHistoryAt(init.closed_at)}）` : ""}
            </div>
          )}
          {init.source && init.source !== "manual" && (
            <div className="mk-source-note">連携元で管理（名前・期間は自動更新されます。それ以外は編集できます）</div>
          )}
        </div>
        <div className="mk-status-btns">
          {STATUS_ORDER.map((st) => {
            const on = init.status === st;
            const c = STATUS_COLOR[st];
            return (
              <button
                key={st}
                className="mk-status-btn"
                style={on ? { background: c, color: "#f7f7f5", borderColor: c } : { color: c }}
                onClick={() => handleStatusClick(st)}
              >
                {STATUS_LABEL[st]}
              </button>
            );
          })}
        </div>
      </div>

      {warn && (
        <div className="mk-warn-bar">
          <span className="mk-warn-text">
            未完了のチェック項目が {warn.open_checks} 件、未完了のタスクが {warn.open_tasks}{" "}
            件あります。通常状態への復帰まで終わっていますか？
          </span>
          <div className="mk-warn-actions">
            <button className="mk-btn-force" onClick={forceComplete}>
              それでも完了にする
            </button>
            <button className="mk-btn-cancel-warn" onClick={() => setWarn(null)}>
              戻る
            </button>
          </div>
        </div>
      )}

      <div className="mk-detail-grid">
        <div>
          {init.source && init.source !== "manual" && (
            <div className="mk-card">
              <h3 className="mk-card-title">連携情報</h3>
              <div className="mk-card-sub" style={{ margin: 0 }}>
                最終同期：{init.source_synced_at ? formatHistoryAt(init.source_synced_at) : "—"}
              </div>
              {sourceDeadlines.length > 0 && (
                <div className="mk-deadline-list">
                  <div className="mk-deadline-heading">検出した期限文言</div>
                  {sourceDeadlines.map((d, i) => (
                    <div className="mk-deadline-item" key={i}>
                      {formatMD(d.date)} {d.time} ／「{d.raw}」
                      {d.step_order !== undefined ? `（step ${d.step_order}）` : ""}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="mk-card">
            <h3 className="mk-card-title">目的・KPI</h3>
            <div className="mk-purpose-text">{init.purpose ?? "（未入力）"}</div>
            {init.kpis.length > 0 && (
              <div className="mk-kpi-grid-4">
                {init.kpis.map((k, i) => (
                  <div className="mk-kpi-cell4" key={i}>
                    <div className="mk-kpi-cell4-label">{k.label}</div>
                    <div className="mk-kpi-cell4-value">{k.value}</div>
                    {k.sub && <div className="mk-kpi-cell4-sub">{k.sub}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mk-card">
            <h3 className="mk-card-title">タイムライン・タスク</h3>
            {sortedTasks.length === 0 && <div className="mk-empty-box">タスクはありません</div>}
            {sortedTasks.map((t) => {
              const overdue = !t.done && isOverdue(t.due_date, t.due_time, today, nowTime);
              const isToday = t.due_date === today;
              const dateColor = t.done ? "#b8b6be" : overdue || isToday ? "#a8442a" : "#45424f";
              const autoClosed = t.done && !!t.closed_by && t.closed_by.startsWith("auto");
              const meta = autoClosed
                ? `自動クローズ${t.close_note ? `：${t.close_note}` : ""}`
                : t.done
                ? "完了"
                : overdue
                ? `期限超過 ${t.due_time}`
                : `確認 ${t.due_time} ／ ${init.owner}`;
              return (
                <div className="mk-task-row" key={t.id}>
                  <div className="mk-task-date" style={{ color: dateColor }}>
                    {formatMD(t.due_date)}
                  </div>
                  <div className="mk-task-body">
                    <span
                      className="mk-task-title"
                      style={{ color: t.done ? "#9d9aa8" : "#1e1c26", textDecoration: t.done ? "line-through" : "none" }}
                    >
                      <span className="mk-task-tag">{TASK_TAG_LABEL[t.tag]}</span>
                      {autoClosed && <span className="mk-task-tag mk-task-tag-auto">自動クローズ</span>}
                      {t.title}
                    </span>
                    <div className="mk-task-meta">{meta}</div>
                  </div>
                  <button
                    className="mk-task-toggle-btn"
                    style={
                      t.done
                        ? { border: "1px solid #dedce2", background: "transparent", color: "#8a8794" }
                        : { border: "none", background: "#1e1c26", color: "#f7f7f5" }
                    }
                    onClick={() => toggleTask(t)}
                  >
                    {t.done ? "戻す" : "完了"}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="mk-card">
            <h3 className="mk-card-title">チェックリスト</h3>
            <div className="mk-card-sub">
              {CHANNEL_LABEL[init.channel]} テンプレートから生成 ／ {checkDone} / {checkTotal} 完了
            </div>
            <div className="mk-checklist-cols">
              {(
                [
                  ["before", "開始前", "#8a8794"],
                  ["during", "実行中", "#2e7d5b"],
                  ["after", "終了時", "#a8442a"],
                ] as const
              ).map(([group, label, headColor]) => (
                <div className="mk-checklist-col" key={group}>
                  <h4 style={{ color: headColor }}>{label}</h4>
                  {init.checklist[group].map((c, idx) => (
                    <button
                      key={idx}
                      className="mk-checklist-item"
                      onClick={() => handleChecklistToggle(group, idx, c.done)}
                    >
                      <span className={`mk-checklist-box ${c.done ? "done" : ""}`}>{c.done ? "✓" : ""}</span>
                      <span style={{ color: c.done ? "#8a8794" : "#1e1c26", textDecoration: c.done ? "line-through" : "none" }}>
                        {c.t}
                      </span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div>
          <div className="mk-next-card">
            <h3 className="mk-card-title">次アクション</h3>
            {nextTask ? (
              <>
                <div className="mk-next-task">{nextTask.title}</div>
                <div className="mk-next-meta">
                  確認日時：{formatMD(nextTask.due_date)} {nextTask.due_time} ／ 確認者：{init.owner}
                </div>
                <div className="mk-next-actions">
                  <button className="mk-btn-gold" onClick={() => toggleTask(nextTask)}>
                    完了にする
                  </button>
                  <button className="mk-btn-ghost-dark" onClick={handleDeferNext}>
                    明日へ延期
                  </button>
                </div>
              </>
            ) : (
              <div style={{ color: "#9d9aa8", fontSize: 13 }}>未完了のタスクはありません。</div>
            )}
          </div>

          <div className="mk-card">
            <h3 className="mk-card-title">関連資料</h3>
            {init.links.length === 0 && <div className="mk-card-sub">関連資料はまだありません</div>}
            {init.links.map((l, i) => (
              <div className="mk-link-row" key={i}>
                <span className="mk-link-kind">{l.kind}</span>
                {l.url && isHttpUrl(l.url) ? (
                  <a href={l.url} target="_blank" rel="noreferrer">
                    {l.label}
                  </a>
                ) : (
                  <span>{l.label}</span>
                )}
              </div>
            ))}
            <div className="mk-desktop-only">
              {linkForm.open ? (
                <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                  <input
                    placeholder="種別（例：LP / 素材 / LINE）"
                    value={linkForm.kind}
                    onChange={(e) => setLinkForm((f) => ({ ...f, kind: e.target.value }))}
                  />
                  <input
                    placeholder="リンク名"
                    value={linkForm.label}
                    onChange={(e) => setLinkForm((f) => ({ ...f, label: e.target.value }))}
                  />
                  <input
                    placeholder="URL（任意）"
                    value={linkForm.url}
                    onChange={(e) => setLinkForm((f) => ({ ...f, url: e.target.value }))}
                  />
                  <div style={{ display: "flex", gap: 6 }}>
                    <button className="btn" onClick={() => setLinkForm({ open: false, kind: "", label: "", url: "" })}>
                      キャンセル
                    </button>
                    <button className="btn btn-primary" onClick={submitLink}>
                      追加
                    </button>
                  </div>
                </div>
              ) : (
                <button className="mk-link-add-btn" onClick={() => setLinkForm((f) => ({ ...f, open: true }))}>
                  ＋ リンクを追加
                </button>
              )}
            </div>
          </div>

          <div className="mk-card">
            <h3 className="mk-card-title">成果</h3>
            <div className="mk-card-sub">
              {init.results.some((r) => r.auto)
                ? "「自動」は連携同期による自動取得（編集不可）／それ以外は手入力"
                : "手入力（施策終了後に記録）"}
            </div>
            {!resultsEditing ? (
              <>
                <div className="mk-results-grid">
                  {init.results.length === 0 && <div className="mk-result-label">実績：施策終了後に入力</div>}
                  {init.results.map((r, i) => (
                    <div key={i}>
                      <div className="mk-result-label">
                        {r.label}
                        {r.auto && <span className="mk-result-auto-tag">自動</span>}
                      </div>
                      <div className="mk-result-value">{r.value}</div>
                    </div>
                  ))}
                </div>
                {init.results.some((r) => r.auto) && init.source_synced_at && (
                  <div className="mk-card-sub" style={{ marginTop: 8, marginBottom: 0 }}>
                    自動項目の最終同期：{formatHistoryAt(init.source_synced_at)}
                  </div>
                )}
                <button
                  className="mk-link-add-btn mk-desktop-only"
                  style={{ marginTop: 10 }}
                  onClick={() => setResultsEditing(true)}
                >
                  成果を編集
                </button>
              </>
            ) : (
              <div className="mk-desktop-only" style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
                {resultsDraft.map((r, i) => (
                  <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <input
                      style={{ flex: 1, minWidth: 0 }}
                      placeholder="項目名"
                      value={r.label}
                      disabled={r.auto}
                      onChange={(e) =>
                        setResultsDraft((prev) => prev.map((row, j) => (j === i ? { ...row, label: e.target.value } : row)))
                      }
                    />
                    <input
                      style={{ flex: 1, minWidth: 0 }}
                      placeholder="値"
                      value={r.value}
                      disabled={r.auto}
                      onChange={(e) =>
                        setResultsDraft((prev) => prev.map((row, j) => (j === i ? { ...row, value: e.target.value } : row)))
                      }
                    />
                    {r.auto && <span className="mk-result-auto-tag" style={{ flexShrink: 0 }}>自動</span>}
                  </div>
                ))}
                <button className="mk-link-add-btn" onClick={addResultRow}>
                  ＋ 項目を追加
                </button>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    className="btn"
                    onClick={() => {
                      setResultsEditing(false);
                      setResultsDraft(init.results);
                    }}
                  >
                    キャンセル
                  </button>
                  <button className="btn btn-primary" onClick={saveResults}>
                    保存
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="mk-card">
            <h3 className="mk-card-title">振り返り・次回改善</h3>
            <div className="mk-desktop-only">
              <textarea
                className="mk-review-textarea"
                value={reviewDraft}
                onChange={(e) => setReviewDraft(e.target.value)}
                onBlur={handleReviewBlur}
              />
            </div>
          </div>

          <div className="mk-card">
            <h3 className="mk-card-title">履歴</h3>
            {history.length === 0 && <div className="mk-card-sub">履歴はありません</div>}
            {history.map((h) => (
              <div className="mk-history-row" key={h.id}>
                <div className="mk-history-at">{formatHistoryAt(h.at)}</div>
                <div style={{ color: h.kind === "alert" ? "#a8442a" : h.kind === "system" ? "#8a8794" : "#45424f" }}>
                  {h.text}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Toast message={toastMessage} />
    </div>
  );
}
